import { EditorView, Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./live-preview/cursor";
import {
  autocompletion,
  acceptCompletion,
  startCompletion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete";
import { keymap } from "@codemirror/view";

// --- Context Reference Decorations (integrated into live preview pipeline) ---

// Matches @[Label](ref:type:id)
const contextRefPattern = /\@\[([^\]\n]+)\]\(ref:([^:)\n]+):([^)\n]+)\)/g;
const contextRefChipMark = Decoration.mark({ class: "cm-context-ref" });

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

/**
 * Build decorations for @[Label](ref:type:id) context references.
 *
 * Uses the same pattern as buildWikilinkDecorations:
 * - When cursor is on the same line, show raw @[Label](ref:type:id)
 * - Otherwise, hide the syntax and show a styled chip
 *
 * Called from the live preview plugin alongside other decoration builders.
 *
 * Also populates `excludeRanges` so the link builder can skip Link nodes
 * that overlap with context references (the markdown parser sees
 * [label](ref:...) as a regular link).
 */
export function buildContextRefDecorations(
  state: EditorState,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
  excludeRanges?: Array<{ from: number; to: number }>,
): void {
  const cursorLine = cursor.cursorLine;
  const text = state.doc.sliceString(range.from, range.to);

  let match: RegExpExecArray | null;
  contextRefPattern.lastIndex = 0;

  while ((match = contextRefPattern.exec(text)) !== null) {
    const start = range.from + match.index;
    const end = start + match[0].length;
    const nodeLine = state.doc.lineAt(start).number;

    // Track the range even if cursor is on this line, so the link
    // builder always skips it (prevents flickering conflicts).
    if (excludeRanges) {
      excludeRanges.push({ from: start, to: end });
    }

    // If cursor is on this line, show raw markdown (no decorations)
    if (cursorLine === nodeLine) continue;

    const label = match[1];
    // Find label position: @[ then label then ](ref:type:id)
    const labelStart = start + 2; // after @[
    const labelEnd = labelStart + label.length; // before ]

    // Hide @[
    decorations.push({
      from: start,
      to: labelStart,
      decoration: Decoration.replace({}),
    });

    // Style label as chip
    decorations.push({
      from: labelStart,
      to: labelEnd,
      decoration: contextRefChipMark,
    });

    // Hide ](ref:type:id)
    decorations.push({
      from: labelEnd,
      to: end,
      decoration: Decoration.replace({}),
    });
  }
}

// --- Autocomplete ---

interface ContextRefResult {
  title: string;
  type: string;
  id: string;
  subtitle?: string;
}

let refSearchCache: Map<string, { results: ContextRefResult[]; ts: number }> = new Map();
const REF_CACHE_TTL = 10000;

async function fetchContextRefs(
  query: string,
  type?: string,
  workflowId?: string,
): Promise<ContextRefResult[]> {
  const cacheKey = `${workflowId || ""}:${type || ""}:${query}`;
  const cached = refSearchCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < REF_CACHE_TTL) {
    return cached.results;
  }

  try {
    let url = `/api/context-references/search?q=${encodeURIComponent(query)}`;
    if (type) url += `&type=${encodeURIComponent(type)}`;
    if (workflowId) url += `&workflow_id=${encodeURIComponent(workflowId)}`;
    const resp = await fetch(url);
    if (!resp.ok) return [];
    const data = await resp.json();
    const results: ContextRefResult[] = data.results || [];
    refSearchCache.set(cacheKey, { results, ts: Date.now() });
    return results;
  } catch {
    return [];
  }
}

/**
 * Parse the text after @ to extract optional type prefix and query.
 * Examples:
 *   "@task:berlin" → { type: "task", query: "berlin" }
 *   "@berlin"      → { type: undefined, query: "berlin" }
 */
function parseAtQuery(text: string): { type?: string; query: string } {
  const colonIndex = text.indexOf(":");
  if (colonIndex > 0) {
    return {
      type: text.slice(0, colonIndex),
      query: text.slice(colonIndex + 1),
    };
  }
  return { query: text };
}

async function contextRefCompletionsInner(
  context: CompletionContext,
  workflowId?: string,
): Promise<CompletionResult | null> {
  const line = context.state.doc.lineAt(context.pos);
  const textBefore = line.text.slice(0, context.pos - line.from);

  // Find the last @ that starts a context reference
  // Must be at start of line or after whitespace (not in middle of word)
  let atIndex = -1;
  for (let i = textBefore.length - 1; i >= 0; i--) {
    if (textBefore[i] === "@") {
      // Valid if at start or preceded by whitespace
      if (i === 0 || /\s/.test(textBefore[i - 1])) {
        atIndex = i;
      }
      break;
    }
    // Stop if we hit a space (means this is not an @ trigger)
    // But allow colons (for type:query) and alphanumeric
    if (!/[\w:@\-]/.test(textBefore[i])) break;
  }

  if (atIndex === -1) return null;

  const afterAt = textBefore.slice(atIndex + 1);

  // Don't trigger if this looks like it's already a completed reference
  if (afterAt.includes("[") || afterAt.includes("]")) return null;

  const { type, query } = parseAtQuery(afterAt);
  const from = line.from + atIndex;

  const results = await fetchContextRefs(query, type, workflowId);
  if (results.length === 0 && afterAt.length === 0) {
    // Still show the autocomplete menu even with no results when just typing @
    // so the user sees it's working. Return null only if we tried a query.
    return null;
  }
  if (results.length === 0) return null;

  return {
    from,
    to: context.pos,
    options: results.map((r) => ({
      label: r.title,
      detail: r.subtitle || r.type,
      apply: `@[${r.title}](ref:${r.type}:${r.id})`,
      type: "text",
    })),
    filter: false, // Server already filters
  };
}

export async function contextRefCompletions(
  context: CompletionContext,
): Promise<CompletionResult | null> {
  return contextRefCompletionsInner(context);
}

export const contextRefAutocomplete = autocompletion({
  override: [contextRefCompletions],
  activateOnTyping: true,
});

/**
 * Re-triggers autocomplete on deletion when the cursor is still after an @ trigger.
 * Without this, backspace closes the autocomplete panel because CM only re-triggers
 * on insertion, not deletion.
 */
export const contextRefRetrigger = EditorView.updateListener.of((update) => {
  if (!update.docChanged) return;
  // Only care about deletions (transaction that makes doc shorter)
  const isDelete = update.transactions.some(
    (tr) => tr.isUserEvent("delete") || tr.isUserEvent("delete.backward"),
  );
  if (!isDelete) return;

  const pos = update.state.selection.main.head;
  const line = update.state.doc.lineAt(pos);
  const textBefore = line.text.slice(0, pos - line.from);

  // Check if we're still in an @ context
  for (let i = textBefore.length - 1; i >= 0; i--) {
    if (textBefore[i] === "@") {
      if (i === 0 || /\s/.test(textBefore[i - 1])) {
        // Still in @ context — re-trigger completion
        setTimeout(() => startCompletion(update.view), 50);
      }
      return;
    }
    if (!/[\w:@\-]/.test(textBefore[i])) return;
  }
});

export const contextRefKeymap = keymap.of([{ key: "Tab", run: acceptCompletion }]);

/**
 * Insert @ at cursor and trigger autocomplete.
 * Used by the toolbar button.
 */
export function insertContextRef(view: EditorView): void {
  const { from, to } = view.state.selection.main;
  view.dispatch({
    changes: { from, to, insert: "@" },
    selection: { anchor: from + 1 },
  });
  setTimeout(() => startCompletion(view), 50);
}

// --- Extensions ---

/**
 * Context reference extensions: autocomplete + keymap.
 * Decoration is handled by the live preview plugin via buildContextRefDecorations.
 */
/**
 * Returns a completion source that includes the workflowId parameter
 * in the search request, enabling @step: references scoped to the workflow.
 */
export function contextRefCompletionsWithWorkflow(workflowId: string) {
  return (context: CompletionContext): Promise<CompletionResult | null> => {
    return contextRefCompletionsInner(context, workflowId);
  };
}

export const contextRefExtensions = [contextRefRetrigger, contextRefKeymap];
